import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState } from "react";
import { XMarkIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import SimpleButton from "./SimpleButton";
import TextArea from "../forms/TextArea";

interface RejectModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  onConfirm: (reason: string) => void;
}

export default function RejectModal({
  show,
  setShow,
  onConfirm,
}: RejectModalProps) {
  const [reason, setReason] = useState("");

  const handleConfirm = () => {
    if (!reason.trim()) {
      return;
    }
    onConfirm(reason);
    setReason("");
    setShow(false);
  };

  const handleCancel = () => {
    setReason("");
    setShow(false);
  };

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleCancel}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform rounded-2xl bg-white border-4 border-red-500 shadow-2xl transition-all w-full max-w-md p-6 sm:p-8">
                {/* Close Button */}
                <button
                  type="button"
                  onClick={handleCancel}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>

                {/* Content */}
                <div>
                  {/* Icon */}
                  <div className="mx-auto flex items-center justify-center mb-4">
                    <ExclamationTriangleIcon className="h-12 w-12 text-red-500" />
                  </div>

                  {/* Title */}
                  <Dialog.Title as="h3" className="text-xl sm:text-2xl font-bold text-dark-grey mb-3 text-center">
                    Reject Content
                  </Dialog.Title>

                  {/* Message */}
                  <p className="text-sm sm:text-base text-dark-grey mb-4 text-center">
                    Please provide a reason for rejecting this content:
                  </p>

                  {/* Textarea */}
                  <div className="mb-6">
                    <TextArea
                      id="rejection-reason"
                      label=""
                      showLabel={false}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Enter rejection reason (required)..."
                      rows={4}
                      borderClass="border-2 border-gray-300 focus:border-red-500"
                    />
                    {!reason.trim() && (
                      <p className="text-xs text-gray-500 mt-1">
                        Reason is required to reject content
                      </p>
                    )}
                  </div>

                  {/* Buttons */}
                  <div className="flex flex-col-reverse sm:flex-row gap-3 justify-center">
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="px-5 py-2.5 rounded-full border-2 border-gray-300 bg-white text-dark-grey font-semibold hover:bg-gray-50 transition"
                    >
                      Cancel
                    </button>
                    <SimpleButton
                      text="Reject Content"
                      colour="red"
                      onClick={handleConfirm}
                      disabled={!reason.trim()}
                      className="min-w-[140px]"
                    />
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
